import { OutlineButton, PrimaryButton } from "@components/Buttons";
import { CustomInput, CustomDatePicker } from "@components/Forms";
import ToggleSwitch from "@components/Forms/ToggleSwitch";
import { DragFilledIcon } from "@components/Icons";
import { ChevronRightIcon as ChevronRightSolidIcon } from "@heroicons/react/24/solid";
import { TableForProductManagement } from "@components/Tables";
import { Breadcrumb, ConfirmActionModal } from "@components/UI";
import AssignProjectModal from "@components/UI/AssignProjectModal";
import ViewMergeModal from "@components/UI/ViewMergeModal";
import DocumentInfoModal from "@components/UI/DocumentInfoModal";
import AddGroupModal from "@components/UI/AddGroupModal";
import ConfirmDeleteModal from "@components/UI/ConfirmDeleteModal";
import ProductProjectsSection from "@components/Product/ProductProjectsSection";
import queryKeys from "@configs/react-query/query-key";
import { showErrorToast, showSuccessToast } from "@configs/react-toastify/snackbar";
import { useFetchData } from "@hooks/useFetchData";
import { useMutationData } from "@hooks/useMutationData";
import { useTheme } from "@hooks/useTheme";
import { useSearchInfo } from "@hooks/useSearchInfo";
import { formatDate, formatDateTime, truncateText, truncateByScreenSize, deduplicateComponents, formatVerificationType } from "@utils/common";
import UserNameWithId from "@components/UI/UserNameWithId";
import { formatUserNameWithId } from "@utils/userDisplay";
import CustomSelect from "@components/UI/CustomSelect";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { DeleteIcon, DocumentInfoIcon } from "@components/Icons";
import { DropIcon } from "@components/Icons";
import Select from "react-select";
import { ITEMS_PER_PAGE_KEY, DEFAULT_ITEMS_PER_PAGE } from "@constants/table";
import { LOCAL_STORAGE, LOGIN_CONTEXT } from "@constants/index";
import useItemsPerPage from "@hooks/useItemsPerPage";
import LoadingSpinner from "@components/UI/LoadingSpinner";
import { useRoutePermissions } from "@contexts/RoutePermissionContext";
import { PermissionContext } from "@contexts/PermissionContext";
import { useContext, useEffect, useState, useRef, useMemo } from "react";
import { translateDepartment } from "@utils/common";
import { useQueryClient } from "@tanstack/react-query";

export default function ProductDetail() {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { productSeq } = useParams();
  const navigate = useNavigate();
  const { creatorList, departmentList, fetchSearchInfo, defaultProduct } = useSearchInfo();
  const { hasAddPermission, hasEditPermission } = useRoutePermissions();
  const { hasManagePermission, permissions } = useContext(PermissionContext);
  const queryClient = useQueryClient();
  // ===== PRODUCT STATE =====
  const [productData, setProductData] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [isUnchanged, setIsUnchanged] = useState(true);
  const [hasErrors, setHasErrors] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [showDefaultProductConfirmModal, setShowDefaultProductConfirmModal] = useState(false);
  const [verificationToDelete, setVerificationToDelete] = useState(null);
  const [isDeleteVerificationModalOpen, setIsDeleteVerificationModalOpen] = useState(false);

  // ===== MODAL STATE =====
  const [isProjectsAndMembersModalOpen, setIsProjectsAndMembersModalOpen] = useState(false);
  const [isAssignProjectModalOpen, setIsAssignProjectModalOpen] = useState(false);
  const [isViewMergeModalOpen, setIsViewMergeModalOpen] = useState(false);
  const [isGroupViewMergeModalOpen, setIsGroupViewMergeModalOpen] = useState(false);
  const [isDocumentInfoModalOpen, setIsDocumentInfoModalOpen] = useState(false);
  const [isAddGroupModalOpen, setIsAddGroupModalOpen] = useState(false);
  const [isEditGroupModalOpen, setIsEditGroupModalOpen] = useState(false);
  const [isDeleteGroupModalOpen, setIsDeleteGroupModalOpen] = useState(false);
  const [selectedVerificationForDocumentInfo, setSelectedVerificationForDocumentInfo] = useState(null);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [editingGroup, setEditingGroup] = useState(null);

  // ===== INCLUDED VERIFICATIONS STATE =====
  const [includedVerifications, setIncludedVerifications] = useState([]);
  const [verificationsTotal, setVerificationsTotal] = useState([]);
  const [verificationsCount, setVerificationsCount] = useState(0); // Total count for default group

  // ===== COMPONENTS STATE =====
  const [componentsData, setComponentsData] = useState([]);
  const [documentInfoComponents, setDocumentInfoComponents] = useState([]);
  const [documentInfoLoading, setDocumentInfoLoading] = useState(false);
  const [groupMergedComponents, setGroupMergedComponents] = useState([]);

  // ===== GROUP DRAG & DROP STATE =====
  const [groupData, setGroupData] = useState({});
  const [activeGroupId, setActiveGroupId] = useState(null);

  // ===== GROUP TABLE PAGINATION STATE (Client-side) =====
  const [groupTableCurrentPage, setGroupTableCurrentPage] = useState(1);
  const { itemsPerPage: groupTableItemsPerPage, handleItemsPerPageChange } = useItemsPerPage(ITEMS_PER_PAGE_KEY.PRODUCT_GROUP_TABLE_ITEM_LIST, DEFAULT_ITEMS_PER_PAGE);

  // ===== GROUP LOADING STATE =====
  const [isLoadingGroupVerifications, setIsLoadingGroupVerifications] = useState(false);

  // ===== DRAG & DROP STATE =====
  const [dragOverGroupId, setDragOverGroupId] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  // ===== REF TO TRACK INITIAL LOAD =====
  const isInitialGroupLoadRef = useRef(true);

  // ===== SCREEN SIZE STATE FOR RESPONSIVE TRUNCATE =====
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  // Listen for window resize
  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Reset component state whenever product is cloned
  useEffect(() => {
    if (!productSeq) return;

    setProductData(null);
    setIsEditMode(false);
    setEditFormData({});
    setIsUnchanged(true);
    setHasErrors(false);
    setIncludedVerifications([]);
    setVerificationsTotal([]);
    setVerificationsCount(0);
    setComponentsData([]);
    setGroupMergedComponents([]);
    setGroupData({});
    setActiveGroupId(null);
    setGroupTableCurrentPage(1);
    isInitialGroupLoadRef.current = true;
  }, [productSeq]);

  // Filter users with DEVELOPER permission for Structure Engineering
  const structureEngineeringList = creatorList.filter(user =>
    user.permissions && user.permissions.includes("DEVELOPER") && user.userId !== "system" && user.enableFlag === "Y"
  );

  // Filter users with PRODUCT_MANAGER permission  
  const productManagerList = creatorList.filter(user =>
    user.permissions && user.permissions.includes("PRODUCT_MANAGER") && user.enableFlag === "Y"
  );

  const developmentDepartmentList = (departmentList || []).map(item => ({
    value: item.value,
    label: translateDepartment(t, item.value),
  }));


  // ===== API CALLS =====
  // Get Product Data for Detail Section
  const getProductData = useFetchData(
    [queryKeys.getProductDetail, productSeq],
    `/product/${productSeq}`,
    {
      method: "GET",
    },
    {},
    {
      enabled: !!productSeq,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );
  // ===== HELPER VARIABLES & FUNCTIONS =====
  const displayProductData = getProductData.data || productData;

  // PRODUCT_MANAGER may edit only products they manage; MANAGER/SUPERUSER may edit all.
  const canEditProductDetail = useMemo(() => {
    if (!hasEditPermission) return false;
    if (hasManagePermission) return true;
    const loggedInUserId = localStorage.getItem(LOCAL_STORAGE.USER_ID);
    const productManagerId = displayProductData?.productManager;
    if (productManagerId == null || productManagerId === "") return false;
    return String(loggedInUserId) === String(productManagerId);
  }, [hasEditPermission, hasManagePermission, displayProductData?.productManager]);

  // Snapshot VEX follows the product VEX endpoint: elevated users, the owning
  // Product Creator, or the assigned Product Manager.
  const canEditProductVex = useMemo(() => {
    if (hasManagePermission) return true;
    const loggedInUserId = localStorage.getItem(LOCAL_STORAGE.USER_ID);
    if (!loggedInUserId) return false;
    const creatorId = displayProductData?.createrId;
    const productManagerId = displayProductData?.productManager;
    const isOwningCreator =
      permissions.includes(LOGIN_CONTEXT.PRODUCT_CREATOR) &&
      creatorId != null &&
      creatorId !== "" &&
      String(creatorId) === String(loggedInUserId);
    const isAssignedManager =
      permissions.includes(LOGIN_CONTEXT.PRODUCT_MANAGER) &&
      productManagerId != null &&
      productManagerId !== "" &&
      String(productManagerId) === String(loggedInUserId);
    return isOwningCreator || isAssignedManager;
  }, [
    hasManagePermission,
    permissions,
    displayProductData?.createrId,
    displayProductData?.productManager,
  ]);

  const canAddProjectFromProductDetail = canEditProductDetail;

  // Creator may rename products they created (route edit is PM/SU-only; name is special-cased like list delete).
  const canEditProductName = useMemo(() => {
    if (canEditProductDetail) return true;
    if (!permissions.includes(LOGIN_CONTEXT.PRODUCT_CREATOR)) return false;
    const loggedInUserId = localStorage.getItem(LOCAL_STORAGE.USER_ID);
    const creatorId = displayProductData?.createrId;
    if (creatorId == null || creatorId === "" || !loggedInUserId) return false;
    return String(loggedInUserId) === String(creatorId);
  }, [canEditProductDetail, permissions, displayProductData?.createrId]);

  // MANAGER/SUPERUSER and PRODUCT_CREATOR who created the product can change Product Manager.
  const canEditProductManagerField = useMemo(() => {
    if (hasManagePermission) return true;
    if (!permissions.includes(LOGIN_CONTEXT.PRODUCT_CREATOR)) return false;
    const loggedInUserId = localStorage.getItem(LOCAL_STORAGE.USER_ID);
    const creatorId = displayProductData?.createrId;
    if (creatorId == null || creatorId === "" || !loggedInUserId) return false;
    return String(loggedInUserId) === String(creatorId);
  }, [hasManagePermission, permissions, displayProductData?.createrId]);

  // ===== LICENSE POLICY STATE =====
  const [selectedLicensePolicySeq, setSelectedLicensePolicySeq] = useState(null);
  const [licensePolicyData, setLicensePolicyData] = useState(null);

  // API call for license policy data - only when selectedLicensePolicySeq is available
  const getLicensePolicyData = useFetchData(
    [queryKeys.getLicensePolicyByLicensePolicySeq, selectedLicensePolicySeq],
    `/license_policy/${selectedLicensePolicySeq}`,
    {
      method: "GET",
    },
    {},
    {
      enabled: !!selectedLicensePolicySeq,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  useEffect(() => {
    if (getProductData?.error) {
      if (getProductData.error?.response?.status === 404) {
        navigate("/404", { state: { backTo: "/products" } });
      }
    }
  }, [getProductData?.error]);

  // API call for updating product
  const updateProduct = useMutationData(
    queryKeys.updateProduct,
    {
      method: "PUT",
    },
    {
      onSuccess: (response) => {
        getProductData.refetch();
        queryClient.invalidateQueries({ queryKey: [queryKeys.vexSnapshot] });
        setIsEditMode(false);
        setEditFormData({});
        showSuccessToast(t("system.update-product-successfully"));
        setIsUnchanged(true);
      },
      onError: (error) => {
        console.error("Error during update product:", error);

        // Check for duplicate product name error
        const errorMessage = error?.response?.data;
        if (error?.response?.status === 400 &&
          errorMessage &&
          (errorMessage.includes("Duplicated product name") || errorMessage.includes("duplicated"))) {
          showErrorToast(t("product.duplicate-product-name-error"), "");
        } else if (
          error?.response?.status === 400 &&
          typeof errorMessage === "string" &&
          errorMessage.includes("The system at least one default product")
        ) {
          showErrorToast(t("product.default-product-at-least-one-error"), "");
        } else {
          showErrorToast(t("system.update-product-error") + ": " + errorMessage, "");
        }
      },
    },
  );

  // API call for getting product verifications
  // Step 2: Get Verifications for Default Group
  const getVerificationsList = useMutationData(
    "merged_product_verification_overview",
    { method: "POST" },
    {
      onSuccess: (response) => {
        // Success handling is now done in fetchVerifications function
      },
      onError: (error) => {
        console.error("Error during get verifications data:", error);
        showErrorToast(t("product.get-product-verification-overview-error"));
      },
    },
  );

  // ===== GROUP API CALLS =====
  // Step 1: Get Groups for Product
  const getGroupsProduct = useFetchData(
    [queryKeys.getGroupsProduct, productSeq],
    `/groups_product/verification_count?productSeq=${productSeq}`,
    {
      method: "GET",
    },
    {},
    {
      enabled: !!productSeq,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );


  // Create Group
  const createGroupProduct = useMutationData(
    "group_product",
    { method: "POST" },
    {
      onSuccess: async (response) => {
        showSuccessToast(t("product.group-created-successfully"));

        // Preserve default group's allItems before refetching
        const defaultGroupItems = groupData['default']?.allItems || [];
        const defaultGroupCount = groupData['default']?.verificationCount || verificationsCount || 0;

        // Refetch groups to get the new group
        await getGroupsProduct.refetch();

        // Ensure default group data is preserved after refetch
        setGroupData(prevGroupData => {
          if (prevGroupData['default'] && defaultGroupItems.length > 0) {
            return {
              ...prevGroupData,
              'default': {
                ...prevGroupData['default'],
                allItems: defaultGroupItems,
                verificationCount: defaultGroupCount
              }
            };
          }
          return prevGroupData;
        });
      },
      onError: (error) => {
        console.error("Error during create group:", error);
        showErrorToast(t("product.create-group-error"));
      },
    },
  );

  // Update Group
  const updateGroupProduct = useMutationData(
    "group_product",
    { method: "PUT" },
    {
      onSuccess: (response) => {
        showSuccessToast(t("product.update-group-success"));
        getGroupsProduct.refetch();
        setIsEditGroupModalOpen(false);
        setEditingGroup(null);
      },
      onError: (error) => {
        console.error("Error during update group:", error);
        showErrorToast(t("product.update-group-error"));
      },
    },
  );

  // Delete Group
  const deleteGroupProduct = useMutationData(
    (groupProductSeq) => `group_product/${groupProductSeq}`,
    { method: "DELETE" },
    {
      onSuccess: async (response) => {
        showSuccessToast(t("product.group-deleted-successfully"));

        // Fetch verifications to update default group count and data
        // (verifications from deleted group will return to default group)
        const { verifications: defaultGroupItems, verificationsCount: defaultGroupCount } = await fetchVerifications();

        // Preserve default group's allItems before refetching
        // Since fetchVerifications already updated the state, we capture the updated data
        const preservedDefaultGroupItems = defaultGroupItems;
        const preservedDefaultGroupCount = defaultGroupCount;

        // Refetch groups to update verification counts
        await getGroupsProduct.refetch();
        await queryClient.invalidateQueries({ queryKey: [queryKeys.vexSnapshot] });
        fetchComponents();
        fetchGroupComponents('default');

        // Ensure default group data is preserved after refetch
        setGroupData(prevGroupData => {
          if (prevGroupData['default'] && preservedDefaultGroupItems.length > 0) {
            return {
              ...prevGroupData,
              'default': {
                ...prevGroupData['default'],
                allItems: preservedDefaultGroupItems,
                verificationCount: preservedDefaultGroupCount
              }
            };
          }
          return prevGroupData;
        });

        // Reset to default group if deleted group was active
        if (activeGroupId && activeGroupId !== 'default') {
          setActiveGroupId('default');
        }
      },
      onError: (error) => {
        console.error("Error during delete group:", error);
        showErrorToast(t("product.delete-group-error"));
      },
    },
  );

  // Assign Verification to Group
  const assignVerificationToGroup = useMutationData(
    "assign_group",
    { method: "POST" },
    {
      onSuccess: (response) => {
        // Success handling is done in the caller (handleDropOnGroup) with specific refetch logic
      },
      onError: (error) => {
        console.error("Error during assign verification to group:", error);
        // Error handling is also done in the caller for better context
      },
    },
  );

  // Fetch ALL verifications for default group (client-side pagination)
  const fetchVerifications = async () => {
    try {
      const response = await getVerificationsList.mutateAsync({
        productSeq: parseInt(productSeq),
        _timestamp: Date.now() // Force fresh API call
      });

      if (typeof response !== "string") {
        const verifications = response.verifications || [];
        const verificationsTotal = response.verificationsTotal || [];
        const verificationsCount = response.verificationsCount || 0;

        setIncludedVerifications(verifications);
        setVerificationsTotal(verificationsTotal);
        setVerificationsCount(verificationsCount);

        // Use verificationsTotal if verifications is empty but count > 0
        // This handles cases where API returns count but verifications array is empty
        const verificationsToUse = (verifications.length === 0 && verificationsCount > 0 && verificationsTotal.length > 0)
          ? verificationsTotal
          : verifications;

        // Update default group with ALL verifications and count
        setGroupData(prevGroupData => ({
          ...prevGroupData,
          'default': {
            ...prevGroupData['default'],
            allItems: verificationsToUse,
            verificationCount: verificationsCount
          }
        }));

        // Return the data for use in callers
        return { verifications: verificationsToUse, verificationsCount };
      }
      return { verifications: [], verificationsCount: 0 };
    } catch (error) {
      console.error("Error fetching verifications:", error);
    }
  };

  // ===== GROUP DATA FUNCTIONS =====
  // API call for getting verifications by group (only for non-default groups)
  const getVerificationsByGroupMutation = useMutationData(
    "merged_product_verification",
    { method: "POST" },
    {
      onError: (error) => {
        console.error("Error during get group verifications:", error);
        showErrorToast(t("product.fetch-group-verifications-error"));
      },
    },
  );

  // Helper function to fetch ALL verifications for a specific group (client-side pagination)
  const fetchGroupVerifications = async (groupProductSeq) => {
    try {
      let result;
      if (!groupProductSeq) {
        // For default group, use the existing getVerificationsList (POST method) - fetch all data
        // Add timestamp to ensure fresh API call and avoid cache
        result = await getVerificationsList.mutateAsync({
          productSeq: parseInt(productSeq),
          _timestamp: Date.now(), // Force fresh API call
          pageSize: groupTableItemsPerPage
        });

        const verifications = result.verifications || [];
        return verifications;
      } else {
        // For other groups, use POST method - fetch all data
        // Add timestamp to ensure fresh API call and avoid cache
        result = await getVerificationsByGroupMutation.mutateAsync({
          productSeq: parseInt(productSeq),
          groupProductSeq: groupProductSeq,
          _timestamp: Date.now() // Force fresh API call
        });
        const verifications = result.verifications || [];
        return verifications;
      }
    } catch (error) {
      console.error("Error fetching group verifications:", error);
      return [];
    }
  };

  // Setup groups data structure (without fetching verifications)
  const fetchAllGroupsData = async () => {
    try {
      if (!productSeq) return;

      // Fetch groups
      const groupsResponse = getGroupsProduct.data || [];

      // Use functional update to ensure we work with latest state
      setGroupData(prevGroupData => {
        // Preserve existing default group data if it exists and has items
        const existingDefaultGroup = prevGroupData['default'];
        const shouldPreserveDefaultData = existingDefaultGroup?.allItems && existingDefaultGroup.allItems.length > 0;

        // Initialize group data with default group, preserving existing allItems
        const initialGroupState = {
          'default': {
            id: 'default',
            name: 'default',
            allItems: shouldPreserveDefaultData ? existingDefaultGroup.allItems : (prevGroupData['default']?.allItems || []),
            verificationCount: existingDefaultGroup?.verificationCount || verificationsCount || 0,
            isDefault: true
          }
        };

        // Add groups from API, preserving existing allItems
        groupsResponse.forEach(group => {
          const existingGroup = prevGroupData[group.groupProductSeq];
          initialGroupState[group.groupProductSeq] = {
            id: group.groupProductSeq,
            name: group.groupProductName || `Group ${group.groupProductSeq}`,
            description: group.groupProductDescription || "",
            color: group.groupProductColor || "#3B82F6",
            allItems: existingGroup?.allItems || [],
            verificationCount: group.verificationCount || 0,
            isDefault: false,
            createdAt: group.createdAt || new Date().toISOString()
          };
        });

        return initialGroupState;
      });

      // Set active group to default if not set
      if (!activeGroupId) {
        setActiveGroupId('default');
      }

    } catch (error) {
      console.error("Error fetching all groups data:", error);
      showErrorToast(t("product.fetch-groups-error"));
    }
  };

  // Step 3: Get Components for View Merge Button
  const getComponentsList = useMutationData(
    "merged_product_component_overview",
    { method: "POST" },
    {
      onSuccess: (response) => {
        if (typeof response !== "string") {
          setComponentsData(deduplicateComponents(response.components) || []);
        }
      },
      onError: (error) => {
        console.error("Error during get components data:", error);
        showErrorToast(t("product.get-product-component-overview-error"));
      },
    },
  );

  const fetchComponents = () => {
    // Manage-only API (View Merge); Creator must not call on Product Detail load.
    if (!canEditProductDetail) return;
    getComponentsList.mutate({
      productSeq: parseInt(productSeq)
    });
  };

  // API call for getting group components (Group Level View Merge)
  const getGroupComponentsList = useMutationData(
    "group_product_component_overview",
    { method: "POST" },
    {
      onSuccess: (response) => {
        if (typeof response !== "string") {
          setGroupMergedComponents(deduplicateComponents(response.components) || []);
        }
      },
      onError: (error) => {
        console.error("Error during get group components data:", error);
        showErrorToast(t("product.get-group-component-overview-error"));
      },
    },
  );

  // API call for getting components by specific verification (for DocumentInfo modal)
  const getComponentsByVerification = useMutationData(
    "merged_product_component_by_verification",
    { method: "POST" },
    {
      onError: (error) => {
        console.error("Error during get components by verification:", error);
        showErrorToast(t("product.get-components-by-verification-error"));
      },
    },
  );

  const fetchGroupComponents = (groupProductSeq) => {
    // Manage-only API (group View Merge); same gate as product-level merge.
    if (!canEditProductDetail) return;
    getGroupComponentsList.mutate({
      productSeq: parseInt(productSeq),
      groupProductSeq: groupProductSeq === 'default' ? null : parseInt(groupProductSeq)
    });
  };


  // API call for saving merged product
  const saveMergedProduct = useMutationData(
    "merged_product",
    { method: "POST" },
    {
      onSuccess: (response) => {
      },
      onError: (error) => {
        console.error("Error during save merged product:", error);
        showErrorToast(t("product.save-merged-product-error"));
      },
    },
  );

  const updateMergedProduct = useMutationData(
    "merged_product",
    { method: "PUT" },
    {
      onSuccess: (response) => {
      },
      onError: (error) => {
        console.error("Error during update merged product:", error);
        showErrorToast(t("product.update-merged-product-error"));
      },
    },
  );

  // API call for deleting verification
  const deleteVerification = useMutationData(
    (mergedProductVerificationSeq) => `/merged_product_verification/${mergedProductVerificationSeq}`,
    { method: "DELETE" },
    {
      onSuccess: (response) => {
        showSuccessToast(t("product.verification-deleted-successfully"));

        // Refresh current group data
        if (activeGroupId) {
          handleGroupSelect(activeGroupId);
        }
      },
      onError: (error) => {
        console.error("Error during delete verification:", error);
        showErrorToast(t("product.delete-verification-error"));
      },
    },
  );

  // API call for cloning product
  const cloneProduct = useMutationData(
    (productSeq) => `/products/${productSeq}/clone`,
    { method: "POST" },
    {
      onSuccess: (response) => {
        // Handle different response structures
        const clonedProductSeq = response?.data?.productSeq;
        if (clonedProductSeq) {
          showSuccessToast(t("product.product-cloned-successfully"));
          navigate(`/products/${clonedProductSeq}`);
        } else {
          console.error("Clone product response missing productSeq:", response);
          showErrorToast(t("product.clone-product-error"));
        }
      },
      onError: (error) => {
        console.error("Error during clone product:", error);
        const errorMessage = error?.response?.data;
        showErrorToast(t("product.clone-product-error") + (errorMessage ? ": " + errorMessage : ""));
      },
    },
  );

  // Fetch merged snapshot components for a single verification (DocumentInfo)
  const fetchDocumentInfoComponents = async (verification) => {
    // Manage-only API; Document Info actions are edit-only.
    if (!canEditProductDetail) {
      setDocumentInfoComponents([]);
      return;
    }
    if (!verification || !productSeq) {
      setDocumentInfoComponents([]);
      return;
    }

    // Check required fields for new API
    if (!verification.projectId || !verification.verificationVersion || !verification.verificationType) {
      console.error("Missing required fields for API call:", {
        projectId: verification.projectId,
        verificationVersion: verification.verificationVersion,
        verificationType: verification.verificationType,
        verification
      });
      setDocumentInfoComponents([]);
      return;
    }

    setDocumentInfoLoading(true);
    setDocumentInfoComponents([]);

    try {
      const response = await getComponentsByVerification.mutateAsync({
        productSeq: parseInt(productSeq),
        projectId: verification.projectId,
        verificationVersion: verification.verificationVersion,
        verificationType: verification.verificationType
      });

      const components = typeof response !== "string" ? response?.components || [] : [];

      setDocumentInfoComponents(components || []);
    } catch (error) {
      console.error("Error fetching document info components:", error);
      setDocumentInfoComponents([]);
    } finally {
      setDocumentInfoLoading(false);
    }
  };

  const refreshProductSnapshotViews = async () => {
    await queryClient.invalidateQueries({ queryKey: [queryKeys.vexSnapshot] });
    fetchComponents();
    if (activeGroupId) {
      fetchGroupComponents(activeGroupId);
    }
    if (isDocumentInfoModalOpen && selectedVerificationForDocumentInfo) {
      await fetchDocumentInfoComponents(selectedVerificationForDocumentInfo);
    }
  };

  // Helper function to normalize values for comparison
  const normalizeValue = (value) => {
    if (value === null || value === undefined || value === "") return "";
    return value;
  };

  // ===== VALIDATION =====
  useEffect(() => {
    const hasRequiredFields = editFormData.productName?.trim() && editFormData.productManager;
    const dataChanged =
      editFormData.productName?.trim() !== displayProductData?.productName?.trim() ||
      editFormData.productManager !== displayProductData?.productManager ||
      editFormData.appliedModel?.trim() !== displayProductData?.appliedModel?.trim() ||
      editFormData.usageScope?.trim() !== displayProductData?.usageScope?.trim() ||
      editFormData.structureEngineer !== displayProductData?.structuralEngineer ||
      normalizeValue(editFormData.inspectionDate) !== normalizeValue(displayProductData?.inspectionDate) ||
      normalizeValue(editFormData.releaseDate) !== normalizeValue(displayProductData?.releaseDate) ||
      editFormData.developmentDepartment !== displayProductData?.developmentDepartment ||
      editFormData.mainContact?.trim() !== displayProductData?.mainContact?.trim() ||
      (editFormData.defaultFlag || "N") !== (displayProductData?.defaultFlag || "N");

    setIsUnchanged(!dataChanged);
    setHasErrors(!hasRequiredFields);
  }, [editFormData, displayProductData]);

  // Step 1: Initial load - fetch search info and groups
  useEffect(() => {
    fetchSearchInfo();
    // Groups will be fetched automatically by getGroupsProduct (useFetchData)
  }, [productSeq]);

  // Step 2: After groups are loaded, fetch verifications and components for default group
  useEffect(() => {
    if (getGroupsProduct.data && productSeq) {
      fetchAllGroupsData();

      // Only fetch verifications and components on initial load, not on subsequent refetches
      // This prevents duplicate API calls when refetching groups (e.g., during drag & drop)
      if (isInitialGroupLoadRef.current) {
        fetchVerifications();
        fetchComponents();
        isInitialGroupLoadRef.current = false; // Mark as loaded
      }
    }
  }, [getGroupsProduct.data, productSeq]);

  // Update verification counts when groups data changes (after refetch)
  useEffect(() => {
    if (getGroupsProduct.data && Object.keys(groupData).length > 0) {
      const groupsResponse = getGroupsProduct.data || [];

      setGroupData(prevGroupData => {
        const updatedGroupData = { ...prevGroupData };

        // Update verificationCount for each group from API
        groupsResponse.forEach(group => {
          if (updatedGroupData[group.groupProductSeq]) {
            updatedGroupData[group.groupProductSeq] = {
              ...updatedGroupData[group.groupProductSeq],
              verificationCount: group.verificationCount || 0
            };
          }
        });

        // Update default group count from verificationsCount state
        if (updatedGroupData['default']) {
          updatedGroupData['default'] = {
            ...updatedGroupData['default'],
            verificationCount: verificationsCount || 0
          };
        }

        return updatedGroupData;
      });
    }
  }, [getGroupsProduct.data, verificationsCount]);

  // Client-side pagination: Calculate displayed items based on current page
  const getDisplayedVerifications = () => {
    if (!activeGroupId || !groupData[activeGroupId]) {
      return [];
    }

    const allItems = groupData[activeGroupId].allItems || [];
    const startIndex = (groupTableCurrentPage - 1) * groupTableItemsPerPage;
    const endIndex = startIndex + groupTableItemsPerPage;

    return allItems.slice(startIndex, endIndex);
  };

  // Calculate pagination info for active group
  const getGroupPaginationInfo = () => {
    if (!activeGroupId || !groupData[activeGroupId]) {
      return { totalData: 0, totalPages: 1 };
    }

    const totalData = groupData[activeGroupId].allItems?.length || 0;
    const totalPages = Math.ceil(totalData / groupTableItemsPerPage) || 1;

    return { totalData, totalPages };
  };

  // Helper function to get user name by ID
  const getUserNameById = (userId) => {
    if (!userId) return "";
    const user = creatorList.find(user => user.value === userId);
    return user ? user.label : userId; // Return name if found, otherwise return ID
  };

  // Helper function to get department name by code
  const getDepartmentNameByCode = (deptCode) => {
    if (!deptCode) return "";
    const dept = departmentList.find(dept => dept.value === deptCode);
    return dept ? dept.label : deptCode; // Return name if found, otherwise return code
  };


  const handleEditClick = () => {
    if (displayProductData) {
      setEditFormData({
        productName: displayProductData.productName || "",
        productManager: displayProductData.productManager || "",
        appliedModel: displayProductData.appliedModel || "",
        usageScope: displayProductData.usageScope || "",
        structureEngineer: displayProductData.structuralEngineer || "",
        inspectionDate: displayProductData.inspectionDate || "",
        releaseDate: displayProductData.releaseDate || "",
        developmentDepartment: displayProductData.developmentDepartment || "",
        mainContact: displayProductData.mainContact || "",
        defaultFlag: displayProductData.defaultFlag || "N",
        useFlag: 1,
      });
      setIsEditMode(true);
    }
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditFormData({});
    if (getProductData.data) {
      setProductData(getProductData.data);
    }
  };

  const shouldConfirmDefaultProductChange = () => {
    if (!hasManagePermission) return false;
    if ((editFormData.defaultFlag || "N") !== "Y") return false;
    if (defaultProduct == null) return false;
    if (Number(productSeq) === defaultProduct.productSeq) return false;
    return true;
  };

  const submitProductEdit = () => {
    const updateData = {
      ...displayProductData,
      productName: editFormData.productName,
      productManager: canEditProductManagerField
        ? editFormData.productManager
        : displayProductData?.productManager,
      appliedModel: editFormData.appliedModel,
      usageScope: editFormData.usageScope,
      structuralEngineer: editFormData.structureEngineer,
      inspectionDate: editFormData.inspectionDate,
      releaseDate: editFormData.releaseDate,
      developmentDepartment: editFormData.developmentDepartment,
      mainContact: editFormData.mainContact,
      projectName: editFormData.projectName,
      defaultFlag: hasManagePermission ? (editFormData.defaultFlag || "N") : displayProductData?.defaultFlag,
    };

    updateProduct.mutate(updateData);
  };

  const handleSaveEdit = () => {
    if (shouldConfirmDefaultProductChange()) {
      setShowDefaultProductConfirmModal(true);
      return;
    }

    submitProductEdit();
  };

  const handleConfirmDefaultProductChange = () => {
    if (defaultProduct == null) return;
    setShowDefaultProductConfirmModal(false);
    submitProductEdit();
  };

  const handleCancelDefaultProductChange = () => {
    setShowDefaultProductConfirmModal(false);
  };

  const handleInputChange = (field, value) => {
    setEditFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Helper function to compare two verifications
  const isSameVerification = (ver1, ver2) => {
    return ver1.projectId === ver2.projectId &&
      ver1.verificationVersion === ver2.verificationVersion &&
      ver1.verificationType === ver2.verificationType;
  };

  // Helper function to categorize verifications for update payload
  const categorizeVerifications = (selectedVerifications, initialVerifications) => {
    const existing = []; // db: true, delete: false
    const removed = [];  // db: true, delete: true  
    const newItems = []; // db: false, delete: false

    // Find existing verifications that are still selected
    selectedVerifications.forEach(selected => {
      const wasExisting = initialVerifications.some(initial =>
        isSameVerification(selected, initial)
      );

      if (wasExisting) {
        // Find the original verification to get mergedProductVerificationSeq
        const originalVerification = initialVerifications.find(initial =>
          isSameVerification(selected, initial)
        );
        existing.push({
          ...selected,
          mergedProductVerificationSeq: originalVerification.mergedProductVerificationSeq
        });
      } else {
        newItems.push(selected);
      }
    });

    // Find existing verifications that were removed
    initialVerifications.forEach(initial => {
      const stillSelected = selectedVerifications.some(selected =>
        isSameVerification(selected, initial)
      );

      if (!stillSelected) {
        removed.push(initial);
      }
    });

    return { existing, removed, newItems };
  };

  const handleAssignProject = async (selectedVerifications) => {
    // Use the selected verifications from modal directly (don't merge with existing)
    // This ensures that unchecked verifications are removed
    const updatedVerifications = selectedVerifications;

    // Process verifications for assignment
    if (updatedVerifications.length > 0) {
      // const newComponentList = await fetchMergedComponents(updatedVerifications); // Removed as mergedProductComponents is not used

      // Determine whether to create new or update existing merged product
      const isFirstTimeAssign = verificationsTotal.length === 0;

      let payload;

      if (isFirstTimeAssign) {
        // For first time assign, use simple payload
        payload = {
          productSeq: parseInt(productSeq),
          verificationFiles: updatedVerifications.map(file => ({
            verificationType: file.verificationType,
            projectSeq: file.projectSeq,
            projectId: file.projectId,
            projectName: file.projectName,
            verificationVersion: file.verificationVersion,
            realFileName: file.realFileName,
            verificationSeq: parseInt(file.verificationSeq)
          })),
        };
      } else {
        // For update, categorize verifications and create proper payload
        const { existing, removed, newItems } = categorizeVerifications(
          selectedVerifications,
          verificationsTotal
        );

        // Fetch components only for selected verifications (existing + still selected + new items)

        // const selectedVerificationsForComponents = [...existing, ...newItems];
        // const selectedComponents = await fetchMergedComponents(selectedVerificationsForComponents);

        const verificationFiles = [
          // Existing + still selected
          ...existing.map(ver => ({
            verificationType: ver.verificationType,
            projectSeq: ver.projectSeq,
            projectId: ver.projectId,
            projectName: ver.projectName,
            verificationVersion: ver.verificationVersion,
            realFileName: ver.realFileName,
            verificationSeq: parseInt(ver.verificationSeq),
            db: true,
            delete: false,
            mergedProductVerificationSeq: ver.mergedProductVerificationSeq
          })),

          // Existing + removed
          ...removed.map(ver => ({
            verificationType: ver.verificationType,
            projectSeq: ver.projectSeq,
            projectId: ver.projectId,
            projectName: ver.projectName,
            verificationVersion: ver.verificationVersion,
            realFileName: ver.realFileName,
            verificationSeq: ver.verificationSeq,
            db: true,
            delete: true,
            mergedProductVerificationSeq: ver.mergedProductVerificationSeq
          })),

          // New items
          ...newItems.map(ver => ({
            verificationType: ver.verificationType,
            projectSeq: ver.projectSeq,
            projectId: ver.projectId,
            projectName: ver.projectName,
            verificationVersion: ver.verificationVersion,
            realFileName: ver.realFileName,
            verificationSeq: parseInt(ver.verificationSeq),
            db: false,
            delete: false
            // No mergedProductVerificationSeq for new items
          }))
        ];

        payload = {
          productSeq: parseInt(productSeq),
          // mergedProductComponents: selectedComponents, // Only components for selected verifications
          verificationFiles
        };
      }

      const mutationToUse = isFirstTimeAssign ? saveMergedProduct : updateMergedProduct;
      const successMessage = isFirstTimeAssign
        ? t("product.assign-project-success")
        : t("product.update-project-success");

      mutationToUse.mutate(payload, {
        onSuccess: async () => {
          setIncludedVerifications(updatedVerifications);

          // Fetch verifications first to update verificationsCount before refetching groups
          const { verifications: defaultGroupItems, verificationsCount: defaultGroupCount } = await fetchVerifications();

          // Preserve default group's allItems before refetching
          // Since fetchVerifications already updated the state, we capture the updated data
          const preservedDefaultGroupItems = defaultGroupItems;
          const preservedDefaultGroupCount = defaultGroupCount;

          // Refetch groups to update verification counts
          await getGroupsProduct.refetch();

          // Ensure default group data is preserved after refetch
          setGroupData(prevGroupData => {
            if (prevGroupData['default'] && preservedDefaultGroupItems.length > 0) {
              return {
                ...prevGroupData,
                'default': {
                  ...prevGroupData['default'],
                  allItems: preservedDefaultGroupItems,
                  verificationCount: preservedDefaultGroupCount
                }
              };
            }
            return prevGroupData;
          });

          await queryClient.invalidateQueries({ queryKey: [queryKeys.vexSnapshot] });
          fetchComponents();
          fetchGroupComponents('default');
          if (isDocumentInfoModalOpen && selectedVerificationForDocumentInfo) {
            await fetchDocumentInfoComponents(selectedVerificationForDocumentInfo);
          }

          // Switch to default group after assigning projects
          setActiveGroupId('default');
          // Reset to first page
          setGroupTableCurrentPage(1);

          // Don't call refetchGroupData since we already have the correct data from fetchVerifications
          // fetchVerifications() already fetched all verifications including updates
          // Calling refetchGroupData might overwrite with empty data due to timing or API issues

          showSuccessToast(successMessage);
        }
      });
    } else {
      // Handle case when all verifications are removed (unchecked)
      // If there were verifications before, we need to send them with delete: true
      if (verificationsTotal.length > 0) {
        // Use categorizeVerifications to properly mark all as removed
        const { removed } = categorizeVerifications([], verificationsTotal);

        const verificationFiles = removed.map(ver => ({
          verificationType: ver.verificationType,
          projectSeq: ver.projectSeq,
          projectId: ver.projectId,
          projectName: ver.projectName,
          verificationVersion: ver.verificationVersion,
          realFileName: ver.realFileName,
          verificationSeq: ver.verificationSeq,
          db: true,
          delete: true,
          mergedProductVerificationSeq: ver.mergedProductVerificationSeq
        }));

        const payload = {
          productSeq: parseInt(productSeq),
          verificationFiles
        };

        updateMergedProduct.mutate(payload, {
          onSuccess: async () => {
            setIncludedVerifications([]);
            setVerificationsTotal([]);
            setComponentsData([]);

            // Clear all group data
            setGroupData(prevGroupData => {
              const updatedGroupData = { ...prevGroupData };
              Object.keys(updatedGroupData).forEach(groupId => {
                if (updatedGroupData[groupId]) {
                  updatedGroupData[groupId] = {
                    ...updatedGroupData[groupId],
                    allItems: []
                  };
                }
              });
              return updatedGroupData;
            });

            // Fetch verifications first to get updated count from API
            await fetchVerifications();

            // Refetch groups to update verification counts
            await getGroupsProduct.refetch();

            // Switch to default group after removing all verifications
            setActiveGroupId('default');
            // Reset to first page
            setGroupTableCurrentPage(1);

            // Force refetch default group to ensure empty state is displayed
            await refetchGroupData('default', false);
            await queryClient.invalidateQueries({ queryKey: [queryKeys.vexSnapshot] });
            fetchComponents();
            fetchGroupComponents('default');
            if (isDocumentInfoModalOpen && selectedVerificationForDocumentInfo) {
              await fetchDocumentInfoComponents(selectedVerificationForDocumentInfo);
            }

            showSuccessToast(t("product.all-verifications-removed"));
          }
        });
      } else {
        // No verifications to remove (was empty before)
        showSuccessToast(t("product.no-verifications-selected"));
      }
    }
  };

  // Function to handle DocumentInfo icon click
  const handleDocumentInfoClick = (verification) => {
    setSelectedVerificationForDocumentInfo(verification);
    setIsDocumentInfoModalOpen(true);

    // Set licensePolicySeq from verification item
    if (verification.licensePolicySeq) {
      setSelectedLicensePolicySeq(verification.licensePolicySeq);
    }

    fetchDocumentInfoComponents(verification);
  };

  const handleCloneProduct = () => {
    if (!displayProductData || !productSeq) {
      showErrorToast(t("product.no-product-data-to-clone"));
      return;
    }
    // Call API to clone product
    cloneProduct.mutate(productSeq);
  };

  // Update productData when API response changes
  useEffect(() => {
    if (getProductData.data) {
      setProductData(getProductData.data);
    }
  }, [getProductData.data]);

  // Update licensePolicyData when API response changes
  useEffect(() => {
    if (getLicensePolicyData.data) {
      setLicensePolicyData(getLicensePolicyData.data);
    }
  }, [getLicensePolicyData.data]);

  const handleRemoveVerification = (verification) => {
    if (!verification) {
      showErrorToast(t("product.no-verification-to-delete"));
      return;
    }

    // Use mergedProductVerificationSeq if available, otherwise use projectSeq as fallback
    const verificationSeq = verification.mergedProductVerificationSeq || verification.projectSeq;

    if (!verificationSeq) {
      showErrorToast(t("product.invalid-verification-id"));
      return;
    }

    // Store verification info for state update after successful deletion
    const verificationToDelete = {
      seq: verificationSeq,
      projectId: verification.projectId,
      verificationVersion: verification.verificationVersion,
      verificationType: verification.verificationType
    };

    deleteVerification.mutate(verificationSeq, {
      onSuccess: async () => {
        // Update includedVerifications by removing the deleted verification
        setIncludedVerifications(prevVerifications =>
          prevVerifications.filter(verification =>
            verification.mergedProductVerificationSeq !== verificationToDelete.seq &&
            !(verification.projectId === verificationToDelete.projectId &&
              verification.verificationVersion === verificationToDelete.verificationVersion &&
              verification.verificationType === verificationToDelete.verificationType)
          )
        );

        // Also update verificationsTotal to keep AssignProjectModal in sync
        setVerificationsTotal(prevVerifications =>
          prevVerifications.filter(verification =>
            verification.mergedProductVerificationSeq !== verificationToDelete.seq &&
            !(verification.projectId === verificationToDelete.projectId &&
              verification.verificationVersion === verificationToDelete.verificationVersion &&
              verification.verificationType === verificationToDelete.verificationType)
          )
        );

        // If deleting from default group, refetch verifications first to update count
        if (activeGroupId === 'default') {
          await fetchVerifications(); // This will update verificationsCount for default group
        }

        // Refetch groups to update verification counts
        await getGroupsProduct.refetch();

        // Refetch active group data to get fresh data from server
        if (activeGroupId) {
          await refetchGroupData(activeGroupId, false);
        }

        await refreshProductSnapshotViews();
      }
    });
  };

  // ===== DRAG & DROP HANDLERS =====
  const handleDragStart = (e, item) => {
    e.dataTransfer.setData("text/plain", JSON.stringify(item));
    e.dataTransfer.effectAllowed = "move";
    setIsDragging(true);
  };

  const handleDragOver = (e, groupId) => {
    e.preventDefault();

    // Don't allow dropping into the same group (active group)
    if (groupId === activeGroupId) {
      e.dataTransfer.dropEffect = "none";
      // Clear any existing highlight
      if (dragOverGroupId !== null) {
        setDragOverGroupId(null);
      }
      return;
    }

    e.dataTransfer.dropEffect = "move";
    // Set highlight on drag over to ensure it stays highlighted
    if (dragOverGroupId !== groupId) {
      setDragOverGroupId(groupId);
    }
  };

  const handleDragLeave = (e, groupId) => {
    e.preventDefault();
    // Only clear if we're actually leaving this specific group
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;

    // Check if mouse is outside the element bounds
    if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
      if (dragOverGroupId === groupId) {
        setDragOverGroupId(null);
      }
    }
  };

  const handleDragEnd = (e) => {
    // Clear states when drag ends
    setIsDragging(false);
    setDragOverGroupId(null);
  };

  const handleDropOnGroup = async (e, targetGroupId) => {
    e.preventDefault();
    setDragOverGroupId(null); // Clear highlight on drop
    setIsDragging(false); // Clear dragging state

    try {
      const itemData = JSON.parse(e.dataTransfer.getData("text/plain"));

      // Use activeGroupId as sourceGroupId since item is being dragged from the currently active table
      // This is more reliable than findItemCurrentGroup which depends on possibly stale state
      const sourceGroupId = activeGroupId;

      if (sourceGroupId === targetGroupId) {
        return;
      }

      // Call API to assign verification to group
      const payload = {
        mergedProductVerificationSeq: itemData.mergedProductVerificationSeq || itemData.projectSeq,
        groupProductSeq: targetGroupId === 'default' ? null : parseInt(targetGroupId)
      };

      // Use mutate with onSuccess callback to refetch both groups
      assignVerificationToGroup.mutate(payload, {
        onSuccess: async () => {
          // Preserve default group data if it's involved
          let preservedDefaultGroupItems = [];
          let preservedDefaultGroupCount = 0;

          // If default group is involved (source or target), refetch verifications FIRST to update count
          // This must be done before getGroupsProduct.refetch() to avoid race condition
          if (sourceGroupId === 'default' || targetGroupId === 'default') {
            const { verifications, verificationsCount } = await fetchVerifications();
            preservedDefaultGroupItems = verifications;
            preservedDefaultGroupCount = verificationsCount;
          }

          // Refetch groups to update verification counts
          await getGroupsProduct.refetch();

          // Ensure default group data is preserved after refetch if it was involved
          if (sourceGroupId === 'default' || targetGroupId === 'default') {
            setGroupData(prevGroupData => {
              if (prevGroupData['default'] && preservedDefaultGroupItems.length > 0) {
                return {
                  ...prevGroupData,
                  'default': {
                    ...prevGroupData['default'],
                    allItems: preservedDefaultGroupItems,
                    verificationCount: preservedDefaultGroupCount
                  }
                };
              }
              return prevGroupData;
            });
          }

          // Refetch source group first (where item was dragged from)
          // But skip if it's default group since we already have the data
          if (sourceGroupId !== 'default') {
            await refetchGroupData(sourceGroupId, false);
          }

          // Then refetch target group (where item was dropped to)
          // But skip if it's default group since we already have the data
          if (targetGroupId !== 'default') {
            await refetchGroupData(targetGroupId, false);
          }

          await refreshProductSnapshotViews();
          showSuccessToast(t("product.verification-assigned-successfully"));
        },
        onError: (error) => {
          console.error("Error during assign verification:", error);
          showErrorToast(t("product.assign-verification-error"));
        }
      });

    } catch (error) {
      console.error("Error during drop:", error);
      showErrorToast(t("product.drop-error"));
    }
  };

  // Helper function to find which group an item currently belongs to
  const findItemCurrentGroup = (item) => {
    for (const [groupId, group] of Object.entries(groupData)) {
      if (group.allItems && group.allItems.some(groupItem =>
        groupItem.mergedProductVerificationSeq === item.mergedProductVerificationSeq ||
        groupItem.projectSeq === item.projectSeq
      )) {
        return groupId;
      }
    }
    return 'default';
  };

  // Helper function to force refetch group data
  const refetchGroupData = async (groupId, showLoading = true) => {
    try {
      if (showLoading) {
        setIsLoadingGroupVerifications(true);
      }

      const groupProductSeq = groupId === 'default' ? null : parseInt(groupId);
      const verifications = await fetchGroupVerifications(groupProductSeq);

      // Update group with ALL fetched verifications
      setGroupData(prevGroupData => ({
        ...prevGroupData,
        [groupId]: {
          ...prevGroupData[groupId],
          allItems: verifications,
        },
      }));
    } catch (error) {
      console.error("Error refetching group data:", error);
      showErrorToast(t("product.fetch-group-verifications-error"));
    } finally {
      if (showLoading) {
        setIsLoadingGroupVerifications(false);
      }
    }
  };

  const handleGroupSelect = async (groupId) => {
    setActiveGroupId(groupId);
    // Reset pagination to first page when switching groups
    setGroupTableCurrentPage(1);

    // If data already loaded, don't fetch again
    if (groupData[groupId]?.allItems && groupData[groupId].allItems.length > 0) {
      return;
    }

    await refetchGroupData(groupId, true);
  };

  // Handler for group table items per page change
  const handleItemsPerPageChangeWithReset = (newItemsPerPage) => {
    handleItemsPerPageChange(newItemsPerPage);
    setGroupTableCurrentPage(1); // Reset to page 1 when changing number of rows
  };

  // Handle create new group
  const handleCreateGroup = (groupData) => {
    createGroupProduct.mutate({
        productSeq: parseInt(productSeq),
        groupProductName: groupData.groupProductName,
        groupProductDescription: groupData.groupProductDescription,
        groupProductColor: groupData.groupProductColor
      },
      {
        onSuccess: (createdGroup) => {
          setGroupData((prev) => ({
            ...prev,
            [createdGroup.groupProductSeq]: createdGroup
          }));
          setActiveGroupId(createdGroup.groupProductSeq);
        },
        onError: (error) => {
          console.error("Failed to create group:", error);
        }
      }
    );
  };

  // Handle edit group
  const handleEditGroup = (groupId) => {
    if (groupId === 'default') {
      showErrorToast(t("product.cannot-edit-default-group"));
      return;
    }
    const group = groupData[groupId];
    if (group) {
      setEditingGroup({
        id: groupId,
        name: group.name,
        description: group.description,
        color: group.color
      });
      setIsEditGroupModalOpen(true);
    }
  };

  // Handle update group
  const handleUpdateGroup = (updatedData) => {
    if (editingGroup) {
      updateGroupProduct.mutate({
        groupProductSeq: parseInt(editingGroup.id),
        productSeq: parseInt(productSeq),
        groupProductName: updatedData.groupProductName,
        groupProductDescription: updatedData.groupProductDescription,
        groupProductColor: updatedData.groupProductColor
      });
    }
  };

  // Handle delete group
  const handleDeleteGroup = (groupId) => {
    if (groupId === 'default') {
      showErrorToast(t("product.cannot-delete-default-group"));
      return;
    }
    setGroupToDelete(groupId);
    setIsDeleteGroupModalOpen(true);
  };

  // Handle confirm delete group
  const handleConfirmDeleteGroup = () => {
    if (groupToDelete) {
      deleteGroupProduct.mutate(groupToDelete);
      setIsDeleteGroupModalOpen(false);
      setGroupToDelete(null);
    }
  };

  // Handle cancel delete group
  const handleCancelDeleteGroup = () => {
    setIsDeleteGroupModalOpen(false);
    setGroupToDelete(null);
  };

  if (getProductData.isFetching) {
    return (
      <LoadingSpinner />
    );
  }

  const handleCloneModalClose = () => {
    setShowCloneModal(false);
  }

  const handleCloneConfirm = () => {
    handleCloneProduct();
    setShowCloneModal(false);
  }

  const handleDeleteVerification = (verification) => {
    setVerificationToDelete(verification);
    setIsDeleteVerificationModalOpen(true);
  };

  const handleCancelDeleteVerification = () => {
    setIsDeleteVerificationModalOpen(false);
    setVerificationToDelete(null);
  };

  const handleConfirmDeleteVerification = () => {
    if (verificationToDelete) {
      handleRemoveVerification(verificationToDelete);
    }
    setIsDeleteVerificationModalOpen(false);
    setVerificationToDelete(null);
  };

  return (
    <div className={`h-full ${colors.bgSecondary}`} data-testid="product-detail-page">
      {/* Header with Breadcrumbs */}
      <div className="pb-3">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Breadcrumb
              items={[{ label: t("navbar.product-management-list"), href: "/products" }, { label: t("navbar.product-details") }]}
            />
            <div className="flex items-center space-x-3">
              {hasAddPermission && displayProductData?.defaultFlag !== "Y" && (
                <>
                  <OutlineButton
                    onClick={() => {
                      setShowCloneModal(true);
                    }}
                    size="md"
                    disabled={cloneProduct.isPending}
                  >
                    {cloneProduct.isPending ? (
                      <div className="flex items-center">
                        <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-current"></div>
                        {t("common.cloning")}
                      </div>
                    ) : (
                      t("product.clone-product")
                    )}
                  </OutlineButton>
                </>
              )}
              {canEditProductDetail && (
                <>
                  <PrimaryButton
                    onClick={() => {
                      // Product level merge: each component has its own licensePolicy
                      fetchComponents();
                      setIsViewMergeModalOpen(true);
                    }}
                    size="md"
                    disabled={verificationsTotal.length === 0}
                  >
                    {t("product.view-merge")}
                  </PrimaryButton>
                </>
              )}
            </div>
          </div>
          <div>
            <h1 className={`text-2xl truncate font-bold ${colors.textPrimary}`}>
              {getProductData.isPending || !displayProductData ? "Loading..." : displayProductData.productName}
            </h1>
          </div>
        </div>
      </div>

      {/* Product Information Section */}
      <div className={`mb-8 rounded-2xl p-6 border ${colors.bgPrimary} ${colors.borderPrimary}`} data-testid="product-detail-form">
        <div className="mb-4">
          <div className="flex items-center justify-between">
            <h2 className={`text-lg font-semibold ${colors.textPrimary}`}>{t("product.product-information")}</h2>
            {isEditMode ? (
              <div className="flex space-x-3">
                <OutlineButton onClick={handleCancelEdit}>{t("common.cancel-button")}</OutlineButton>
                <PrimaryButton
                  data-testid="product-detail-save-button"
                  onClick={handleSaveEdit}
                  disabled={updateProduct.isPending || isUnchanged || hasErrors}
                  className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${updateProduct.isPending
                    ? colors.buttonDisabledMuted
                    : `${colors.textButton} ${colors.buttonBackgroundGradient}`
                    }`}
                >
                  {updateProduct.isPending ? (
                    <div className="flex items-center">
                      <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                      Saving...
                    </div>
                  ) : (
                    t("common.save-button")
                  )}
                </PrimaryButton>
              </div>
            ) : (
              <OutlineButton onClick={handleEditClick}>{t("project.edit-information")}</OutlineButton>
            )}
          </div>
        </div>

        {/* Product Details Grid */}
        <div className="space-y-2 px-4">
          {/* Row 1 */}
          <div className={`grid grid-cols-5 gap-x-4 gap-y-4 border-t ${colors.borderSection} pt-3`}>
            <div>
              <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>
                {t("product.product-name")}{" "}
                {isEditMode && canEditProductName && <span className={`${colors.textRequired}`}>*</span>}
              </label>
              {isEditMode && canEditProductName ? (
                <CustomInput
                  type="text"
                  value={editFormData.productName || ""}
                  onChange={(e) => handleInputChange("productName", e.target.value)}
                  placeholder={displayProductData?.productName || ""}
                  maxLength={255}
                />
              ) : (
                <p
                  title={displayProductData?.productName}
                  className={`text-sm truncate ${colors.textPrimary} h-10 content-center ${getProductData.isLoading || !displayProductData ? `animate-pulse rounded ${colors.bgSkeleton}` : ""}`}
                >
                  {getProductData.isPending || !displayProductData ? "" : displayProductData.productName}
                </p>
              )}
            </div>
            <div>
              <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>
                {t("product.product-manager")}{" "}
                {isEditMode && canEditProductManagerField && <span className={`${colors.textRequired}`}>*</span>}
              </label>
              {isEditMode ? (
                canEditProductManagerField ? (
                  <CustomSelect
                    options={productManagerList}
                    value={productManagerList.find((opt) => opt.value === editFormData.productManager)}
                    onChange={(opt) => handleInputChange("productManager", opt?.value || null)}
                    placeholder={t("common.please-select")}
                    isSearchable={true}
                    className="w-full text-sm font-medium"
                    displayFormatLabelWithID={true}
                  />
                ) : (
                  <CustomInput
                    type="text"
                    value={
                      getProductData.isPending || !displayProductData
                        ? ""
                        : formatUserNameWithId(
                            getUserNameById(editFormData.productManager ?? displayProductData?.productManager),
                            editFormData.productManager ?? displayProductData?.productManager
                          )
                    }
                    disabled
                  />
                )
              ) : (
                <div
                  className={`min-h-10 flex min-w-0 w-full items-center overflow-hidden ${getProductData.isPending || !displayProductData ? `animate-pulse rounded ${colors.bgSkeleton}` : ""}`}
                >
                  {!(getProductData.isPending || !displayProductData) && (
                    <UserNameWithId
                      name={getUserNameById(displayProductData.productManager)}
                      id={displayProductData.productManager}
                      layout="inline"
                    />
                  )}
                </div>
              )}
            </div>
            {/* <div>
              <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>{t("product.development-department")}</label>
              {isEditMode ? (
                <CustomSelect
                  options={developmentDepartmentList}
                  value={developmentDepartmentList.find((opt) => opt.value === editFormData.developmentDepartment)}
                  onChange={(opt) => handleInputChange("developmentDepartment", opt?.value || null)}
                  placeholder={t("common.please-select")}
                  isSearchable={true}
                  className="w-full text-sm font-medium"
                />
              ) : (
                <p
                  title={translateDepartment(t, displayProductData?.developmentDepartment)}
                  className={`text-sm truncate ${colors.textPrimary} h-10 content-center ${getProductData.isPending || !displayProductData ? `animate-pulse rounded ${colors.bgSkeleton}` : ""}`}
                >
                  {getProductData.isPending || !displayProductData ? "" : translateDepartment(t, displayProductData.developmentDepartment)}
                </p>
              )}
            </div> */}
            <div>
              <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>{t("product.main-contact")}</label>
              {isEditMode ? (
                <CustomInput
                  type="text"
                  value={editFormData.mainContact || ""}
                  onChange={(e) => handleInputChange("mainContact", e.target.value)}
                  maxLength={255}
                />
              ) : (
                <p
                  title={displayProductData?.mainContact}
                  className={`text-sm truncate ${colors.textPrimary} h-10 content-center ${getProductData.isPending || !displayProductData ? `animate-pulse rounded ${colors.bgSkeleton}` : ""}`}
                >
                  {getProductData.isPending || !displayProductData ? "" : displayProductData.mainContact}
                </p>
              )}
            </div>
            {/* <div>
              <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>{t("product.structure-engineering")}</label>
              {isEditMode ? (
                <CustomSelect
                  options={structureEngineeringList}
                  value={structureEngineeringList.find((opt) => opt.value === editFormData.structureEngineer)}
                  onChange={(opt) => handleInputChange("structureEngineer", opt?.value || null)}
                  placeholder={t("common.please-select")}
                  isSearchable={true}
                  className="w-full text-sm font-medium"
                  displayFormatLabelWithID={true}
                />
              ) : (
                <p
                  title={getUserNameById(displayProductData?.structuralEngineer)}
                  className={`text-sm truncate ${colors.textPrimary} h-10 content-center ${getProductData.isPending || !displayProductData ? `animate-pulse rounded ${colors.bgSkeleton}` : ""}`}
                >
                  {getProductData.isPending || !displayProductData ? "" : getUserNameById(displayProductData.structuralEngineer)}
                </p>
              )}
            </div> */}

            <div className="relative">
              <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>{t("product.inspection-date")}</label>
              {isEditMode ? (
                <CustomDatePicker
                  selected={editFormData.inspectionDate ? new Date(editFormData.inspectionDate) : null}
                  onChange={(date) => handleInputChange("inspectionDate", date)}
                  placeholderText={t("project.select-date-placeholder")}
                  dateFormat="yyyy-MM-dd"
                  minDate={new Date()}
                />
              ) : (
                <p
                  title={formatDate(displayProductData?.inspectionDate)}
                  className={`text-sm truncate ${colors.textPrimary} h-10 content-center ${getProductData.isPending || !displayProductData ? `animate-pulse rounded ${colors.bgSkeleton}` : ""}`}
                >
                  {getProductData.isPending || !displayProductData
                    ? ""
                    : displayProductData.inspectionDate
                      ? formatDate(displayProductData.inspectionDate)
                      : ""}
                </p>
              )}
            </div>
            <div>
              <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>
                {t("product.default-product", "Default Product")}
              </label>
              <div className="h-10 flex items-center">
                {isEditMode ? (
                  <div className="flex items-center space-x-2">
                    <ToggleSwitch
                      checked={(editFormData.defaultFlag || "N") === "Y"}
                      onChange={(checked) => handleInputChange("defaultFlag", checked ? "Y" : "N")}
                      disabled={!hasManagePermission}
                    />
                  </div>
                ) : (
                  <p
                    title={displayProductData?.defaultFlag === "Y" ? "Yes" : "No"}
                    className={`text-sm truncate ${colors.textPrimary} h-10 content-center ${getProductData.isPending || !displayProductData ? `animate-pulse rounded ${colors.bgSkeleton}` : ""}`}
                  >
                    {getProductData.isPending || !displayProductData
                      ? ""
                      : displayProductData.defaultFlag === "Y"
                        ? t("common.yes", "Yes")
                        : t("common.no", "No")}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Row 2 */}
          <div className={`grid grid-cols-5 gap-x-4 gap-y-4 border-t ${colors.borderSection} pt-3`}>
            <div className="relative">
              <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>{t("product.release-date")}</label>
              {isEditMode ? (
                <CustomDatePicker
                  selected={editFormData.releaseDate ? new Date(editFormData.releaseDate) : null}
                  onChange={(date) => handleInputChange("releaseDate", date)}
                  placeholderText={t("project.select-date-placeholder")}
                  dateFormat="yyyy-MM-dd"
                  minDate={new Date()}
                />
              ) : (
                <p
                  title={formatDate(displayProductData?.releaseDate)}
                  className={`text-sm truncate ${colors.textPrimary} h-10 content-center ${getProductData.isPending || !displayProductData ? `animate-pulse rounded ${colors.bgSkeleton}` : ""}`}
                >
                  {getProductData.isPending || !displayProductData
                    ? ""
                    : displayProductData.releaseDate
                      ? formatDate(displayProductData.releaseDate)
                      : ""}
                </p>
              )}
            </div>
            <div>
              <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>{t("product.applied-model")}</label>
              {isEditMode ? (
                <CustomInput
                  type="text"
                  value={editFormData.appliedModel || ""}
                  onChange={(e) => handleInputChange("appliedModel", e.target.value)}
                  maxLength={255}
                />
              ) : (
                <p
                  title={displayProductData?.appliedModel}
                  className={`text-sm truncate ${colors.textPrimary} h-10 content-center ${getProductData.isPending || !displayProductData ? `animate-pulse rounded ${colors.bgSkeleton}` : ""}`}
                >
                  {getProductData.isPending || !displayProductData ? "" : displayProductData.appliedModel}
                </p>
              )}
            </div>
            <div>
              <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>{t("product.usage-scope")}</label>
              {isEditMode ? (
                <CustomInput
                  type="text"
                  value={editFormData.usageScope || ""}
                  onChange={(e) => handleInputChange("usageScope", e.target.value)}
                  maxLength={255}
                />
              ) : (
                <p
                  title={displayProductData?.usageScope}
                  className={`text-sm truncate ${colors.textPrimary} h-10 content-center ${getProductData.isPending || !displayProductData ? `animate-pulse rounded ${colors.bgSkeleton}` : ""}`}
                >
                  {getProductData.isPending || !displayProductData ? "" : displayProductData.usageScope}
                </p>
              )}
            </div>
            <div>
              <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>{t("common.creator")}</label>
              {isEditMode ? (
                <CustomInput
                  type="text"
                  value={formatUserNameWithId(displayProductData.createrName, displayProductData.createrId)}
                  onChange={(e) => handleInputChange("createrName", e.target.value)}
                  disabled
                />
              ) : (
                <div
                  className={`min-h-10 flex min-w-0 w-full items-center overflow-hidden ${getProductData.isPending || !displayProductData ? `animate-pulse rounded ${colors.bgSkeleton}` : ""}`}
                >
                  {!(getProductData.isPending || !displayProductData) && (
                    <UserNameWithId name={displayProductData.createrName} id={displayProductData.createrId} layout="inline" />
                  )}
                </div>
              )}
            </div>
            <div>
              <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>
                {t("common.create-at")}
              </label>
              {isEditMode ? (
                <CustomInput
                  type="text"
                  value={displayProductData.createdAt ? formatDateTime(displayProductData.createdAt) : ""}
                  onChange={(e) => handleInputChange("createdAt", e.target.value)}
                  disabled
                />
              ) : (
                <p
                  title={formatDateTime(displayProductData?.createdAt)}
                  className={`text-sm truncate ${colors.textPrimary} h-10 content-center ${getProductData.isPending || !displayProductData ? `animate-pulse rounded ${colors.bgSkeleton}` : ""}`}
                >
                  {getProductData.isPending || !displayProductData
                    ? ""
                    : displayProductData.createdAt
                      ? formatDateTime(displayProductData.createdAt)
                      : ""}
                </p>
              )}
            </div>
          </div>

          {/* Row 3 */}
          <div className={`grid grid-cols-5 gap-x-4 gap-y-4 border-t ${colors.borderSection} pt-3`}>
            <div>
              <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>
                {t("common.last-modified-date")}
              </label>
              {isEditMode ? (
                <CustomInput
                  type="text"
                  value={displayProductData.updatedAt ? formatDateTime(displayProductData.updatedAt) : ""}
                  onChange={(e) => handleInputChange("updaterName", e.target.value)}
                  disabled
                />
              ) : (
                <p
                  title={formatDateTime(displayProductData?.updatedAt)}
                  className={`text-sm truncate ${colors.textPrimary} h-10 content-center ${getProductData.isPending || !displayProductData ? `animate-pulse rounded ${colors.bgSkeleton}` : ""}`}
                >
                  {getProductData.isPending || !displayProductData
                    ? ""
                    : displayProductData.updatedAt
                      ? formatDateTime(displayProductData.updatedAt)
                      : ""}
                </p>
              )}
            </div>
            <div>
              <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>
                {t("common.last-modified-by")}
              </label>
              {isEditMode ? (
                <CustomInput
                  type="text"
                  value={formatUserNameWithId(displayProductData.updaterName, displayProductData.updaterId)}
                  onChange={(e) => handleInputChange("updatedAt", e.target.value)}
                  disabled
                />
              ) : (
                <div
                  className={`min-h-10 flex min-w-0 w-full items-center overflow-hidden ${getProductData.isPending || !displayProductData ? `animate-pulse rounded ${colors.bgSkeleton}` : ""}`}
                >
                  {!(getProductData.isPending || !displayProductData) && (
                    <UserNameWithId name={displayProductData.updaterName} id={displayProductData.updaterId} layout="inline" />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Product Projects Section */}
      <div className="mb-8">
        <ProductProjectsSection
          title={t("product.product-projects")}
          productSeq={productSeq}
          showAddButton={canAddProjectFromProductDetail}
          canEditProductDetail={canEditProductDetail}
        />
      </div>

      {/* Groups and Verifications Section */}
      <div className={`rounded-2xl p-4 ${colors.bgPrimary} ${colors.borderPrimary}`}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className={`text-lg font-semibold ${colors.textPrimary}`}>{t("product.product-groups-management")}</h2>
          {canEditProductDetail && (
            <OutlineButton
              onClick={() => setIsAssignProjectModalOpen(true)}

            >
              {t("product.assign-project")}
            </OutlineButton>
          )}
        </div>

        {/* Main Layout: Sidebar + Content */}
        <div className="flex gap-6">
          {/* Sidebar - Groups */}
          <div className={`w-1/5 rounded-2xl p-4 border ${colors.bgPrimary} ${colors.borderPrimary}`}>
            {/* Add Group Button */}
            {canEditProductDetail && (
              <button
                onClick={() => setIsAddGroupModalOpen(true)}
                className={`w-full flex items-center justify-center space-x-2 p-2.5 mb-4 rounded-md ${colors.buttonBackgroundGradient} ${colors.buttonBackgroundGradientHover} transition-colors`}
              >
                <span className={`text-sm font-semibold`}>
                  {t("product.add-group")}
                </span>
              </button>
            )}
            {/* Groups List */}
            <div className="space-y-2">
              {/* Default Group - Always first */}
              {groupData['default'] && (
                <div
                  data-testid="group-sidebar-item-default"
                  className={`flex items-center justify-between p-2 rounded-lg transition-all duration-200 ${colors.textSecondary} ${dragOverGroupId === 'default'
                    ? `${colors.bgGroupCardActive} shadow-lg scale-105 cursor-pointer`
                    : isDragging && activeGroupId === 'default'
                      ? `${colors.bgSelectedPreviewName} opacity-50 cursor-not-allowed`
                      : `${colors.bgSelectedPreviewName} cursor-pointer ${activeGroupId === 'default'
                        ? `${colors.borderActiveDashed} border`
                        : `${colors.borderPrimary} border-transparent`
                      }`
                    }`}
                  onClick={() => handleGroupSelect('default')}
                  onDragOver={(e) => handleDragOver(e, 'default')}
                  onDragLeave={(e) => handleDragLeave(e, 'default')}
                  onDrop={(e) => handleDropOnGroup(e, 'default')}
                >
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold block" title={t("product.default-group")}>
                        {truncateByScreenSize(t("product.default-group"), 10, 25, 1400, screenWidth)}
                      </div>
                      <div className="text-[12px] italic mt-0.5">
                        {t("product.total-verifications")}: <span className="font-bold">{groupData['default'].verificationCount || 0}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 flex-shrink-0">
                    {/* Chevron Icon for active group */}
                    {activeGroupId === 'default' && (
                      <ChevronRightSolidIcon className={`h-4 w-4 ${colors.textActive}`} />
                    )}
                  </div>
                </div>
              )}

              {/* Other Groups - Render from API response to preserve backend order */}
              {(getGroupsProduct.data || []).map((group) => {
                const groupId = group.groupProductSeq;

                return (
                  <div
                    key={groupId}
                    data-testid={`group-sidebar-item-${groupId}`}
                    className={`relative flex items-center justify-between p-2 rounded-lg transition-all duration-200 ${colors.textSecondary} ${dragOverGroupId === groupId
                      ? `${colors.bgGroupCardActive} shadow-lg scale-105 cursor-pointer`
                      : isDragging && activeGroupId === groupId
                        ? `${colors.bgSelectedPreviewName} opacity-50 cursor-not-allowed`
                        : `${colors.bgSelectedPreviewName} cursor-pointer ${activeGroupId === groupId
                          ? `${colors.borderActiveDashed} border`
                          : `${colors.borderPrimary} border-transparent`
                        }`
                      }`}
                    style={{
                      borderLeftColor: group.groupProductColor || "#3B82F6",
                      borderLeftWidth: '4px'
                    }}
                    onClick={() => handleGroupSelect(groupId)}
                    onDragOver={(e) => handleDragOver(e, groupId)}
                    onDragLeave={(e) => handleDragLeave(e, groupId)}
                    onDrop={(e) => handleDropOnGroup(e, groupId)}
                  >
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold block" title={group.groupProductName}>
                          {truncateByScreenSize(group.groupProductName, 10, 25, 1400, screenWidth)}
                        </div>
                        <div className="text-[12px] italic mt-0.5">
                          {t("product.total-verifications")}: <span className="font-bold">{group.verificationCount || 0}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2 flex-shrink-0">
                      {/* Chevron Icon for active group */}
                      {activeGroupId === groupId && (
                        <ChevronRightSolidIcon className={`h-4 w-4 ${colors.textActive}`} />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Main Content - Table */}
          <div className={`w-4/5 rounded-2xl p-4 border ${colors.bgPrimary} ${colors.borderPrimary}`}>
            {activeGroupId && groupData[activeGroupId] ? (
              <div>
                <div className="mb-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h3
                        className={`text-lg font-semibold ${colors.textPrimary} mb-1`}
                        title={activeGroupId === 'default' ? t("product.default-group") : groupData[activeGroupId].name}
                      >
                        {truncateByScreenSize(activeGroupId === 'default' ? t("product.default-group") : groupData[activeGroupId].name, 30, 50, 1400, screenWidth)}
                      </h3>
                      <p className={`text-sm ${colors.textSecondary} min-h-[20px]`} title={activeGroupId === 'default' ? '' : groupData[activeGroupId].description}>
                        {activeGroupId === 'default'
                          ? t("product.default-group-description")
                          : groupData[activeGroupId].description
                            ? truncateByScreenSize(groupData[activeGroupId].description, 50, 100, 1400, screenWidth)
                            : '\u00A0' // Non-breaking space to preserve layout height when no description
                        }
                      </p>
                    </div>

                    <div className="flex items-center space-x-2 ml-4">
                      {/* Action Dropdown for Edit/Delete Group */}
                      {canEditProductDetail && activeGroupId !== 'default' && (
                        <div className="flex-shrink-0">
                          <Select
                            options={[
                              { label: t("common.edit-button"), value: "edit" },
                              { label: t("common.delete-button"), value: "delete" }
                            ]}
                            value={null}
                            onChange={(selectedOption) => {
                              if (selectedOption) {
                                if (selectedOption.value === "edit") {
                                  handleEditGroup(activeGroupId);
                                } else if (selectedOption.value === "delete") {
                                  handleDeleteGroup(activeGroupId);
                                }
                              }
                            }}
                            placeholder={t("common.action")}
                            isSearchable={false}
                            isClearable={false}
                            className="verification-request-select"
                            styles={{
                              control: (base) => ({
                                ...base,
                                backgroundImage: "linear-gradient(to right, #1EC6FF, #367FFF)",
                                border: "none",
                                borderRadius: "8px",
                                outline: "none",
                                boxShadow: "none",
                                minHeight: "36px",
                                cursor: "pointer",
                                "&:hover": {
                                  border: "none",
                                  outline: "none",
                                  boxShadow: "none",
                                },
                              }),
                              placeholder: (base) => ({
                                ...base,
                                color: "#000000",
                                margin: 0,
                                padding: 0,
                                fontSize: "14px",
                                fontWeight: "500"
                              }),
                              option: (base, state) => ({
                                ...base,
                                backgroundColor: colors.bgPrimaryColor,
                                color: state.isSelected || state.isFocused ? colors.textActiveColor : colors.textPrimaryColor,
                                border: state.isSelected || state.isFocused ? `1px solid ${colors.dropdownBorderColor}` : "1px solid transparent",
                                boxShadow: state.isSelected || state.isFocused ? "0px -0.1px 4px 1px rgba(0, 0, 0, 0.6)" : "none",
                                borderRadius: "0.25rem",
                                padding: "8px 12px",
                                cursor: "pointer",
                                fontSize: "14px",
                                fontWeight: "500",
                                marginBottom: "3px",
                                position: "relative",
                                zIndex: state.isSelected || state.isFocused ? 1000 : 0,
                                transition: "all 0.2s ease",
                                "&:hover": {
                                  backgroundColor: colors.bgPrimaryColor,
                                  color: colors.textActiveColor
                                }
                              }),
                              menu: (base) => ({
                                ...base,
                                backgroundColor: colors.bgPrimaryColor,
                                borderRadius: "0.25rem",
                                marginTop: 4,
                                borderTop: "none",
                                boxShadow: "0px 0px 6px 1px rgba(0, 0, 0, 0.6)",
                                padding: "0.125rem",
                              }),
                              menuList: (base) => ({
                                ...base,
                                borderRadius: "0.5rem",
                                padding: "0.5rem",
                                margin: 0,
                                backgroundColor: colors.bgPrimaryColor,
                                color: colors.textPrimaryColor,
                              }),
                              indicatorSeparator: () => ({
                                display: "none"
                              }),
                              dropdownIndicator: (base) => ({
                                ...base,
                                color: "#000000",
                                "&:hover": {
                                  color: "#000000"
                                }
                              }),
                              valueContainer: (base) => ({
                                ...base,
                                padding: "0 12px",
                                paddingRight: 0
                              })
                            }}
                          />
                        </div>
                      )}

                      {/* Merged Group Component Button */}
                      {canEditProductDetail && (
                        <OutlineButton
                          onClick={() => {
                            // Fetch components for the current group via API
                            // Each component has its own licensePolicy, no need to fetch a common one
                            fetchGroupComponents(activeGroupId);
                            setIsGroupViewMergeModalOpen(true);
                          }}
                          size="md"
                          disabled={(groupData[activeGroupId].allItems?.length || 0) === 0}
                          className={(groupData[activeGroupId].allItems?.length || 0) === 0 ? "cursor-not-allowed opacity-50 hover:opacity-50" : ""}
                        >
                          {t("product.merged-group-component")}
                        </OutlineButton>
                      )}
                    </div>
                  </div>
                </div>

                {(groupData[activeGroupId].allItems?.length || 0) === 0 ? (
                  <div className={`flex text-center justify-center align-center h-40 ${colors.borderPrimary} ${colors.bgPrimary} rounded-lg border`}>
                    <div className="flex flex-col items-center justify-center h-full">
                      <div className="mb-2 flex justify-center">
                        <DropIcon className={`h-16 w-16 ${colors.textActive}`} />
                      </div>
                      <p className={`text-sm ${colors.textSecondary}`}>
                        {activeGroupId === 'default' || !canEditProductDetail
                          ? t("product.no-verification-information-default")
                          : t("product.no-verifications-in-group")
                        }
                      </p>
                    </div>
                  </div>
                ) : (
                  <TableForProductManagement
                    columns={canEditProductDetail ? ["dragHandle", "projectId", "projectName", "verificationVersion", "verificationType", "createrId", "createdAt", "action"] : ["projectId", "projectName", "verificationVersion", "verificationType", "createrId", "createdAt"]}
                    columnConfig={{
                      dragHandle: canEditProductDetail && {
                        style: { width: "40px" },
                        headerRender: () => "",
                        hideBorder: true,
                        customRender: () => (
                          <div className="flex items-center justify-center cursor-move">
                            <DragFilledIcon className={`h-4 w-4 ${colors.iconMuted}`} />
                          </div>
                        ),
                      },
                      projectId: {
                        style: { width: "15%" },
                        title: t("project.project-id"),
                        customRender: (value) => value || "-",
                      },
                      projectName: {
                        style: { width: "20%" },
                        title: t("project.project-name"),
                        customRender: (value) => value || "-",
                      },
                      verificationVersion: {
                        style: { width: "10%" },
                        title: t("common.version"),
                        customRender: (value) => value || "-",
                      },
                      verificationType: {
                        style: { width: "15%" },
                        title: t("common.verification-type"),
                        customRender: (value) => formatVerificationType(value) || "-",
                      },
                      createrId: {
                        style: { width: "15%" },
                        title: t("common.creator"),
                        customRender: (value, row) => (
                          <UserNameWithId name={row.createrName} id={value || row.createrId} />
                        ),
                      },
                      createdAt: {
                        style: { width: "15%" },
                        title: t("common.create-at"),
                        customRender: (value) => value ? formatDateTime(value) : "-",
                      },
                      action: {
                        style: {
                          width: "96px",
                          whiteSpace: "normal",
                          overflowWrap: "initial",
                          position: "sticky",
                          right: "0",
                          zIndex: "10",
                          backgroundColor: "inherit",
                        },
                        headerRender: () => t("common.action"),
                        customRender: (value, row) => (
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDocumentInfoClick(row);
                              }}
                              className={`${colors.textMuted} ${colors.hoverTextNeutral}`}
                            >
                              <DocumentInfoIcon className="h-4.5 w-4.5" />
                            </button>
                            <div className={`w-px h-3 ${colors.bgCommunicationFilterButton} self-center`}></div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteVerification(row);
                              }}
                              className={`${colors.textDanger} ${colors.hoverTextDangerStrong}`}
                            >
                              <DeleteIcon className="h-4 w-4" />
                            </button>
                          </div>
                        ),
                      },
                    }}
                    data={getDisplayedVerifications()}
                    totalPages={getGroupPaginationInfo().totalPages}
                    totalData={getGroupPaginationInfo().totalData}
                    currentPage={groupTableCurrentPage}
                    setCurrentPage={setGroupTableCurrentPage}
                    loading={isLoadingGroupVerifications}
                    maxRowsPerPage={groupTableItemsPerPage}
                    showPagination={true}
                    hasCheckbox={false}
                    shouldShowGoToPage={true}
                    onItemsPerPageChange={handleItemsPerPageChangeWithReset}
                    showCheckboxOnlyWhenSelected={true}
                    rowKey={(row) => `${row.projectId}-${row.verificationVersion}-${row.verificationType}`}
                    customRowRender={(row, index, children) => {
                      const getRowStripeColor = (index) => {
                        return index % 2 === 0 ? colors.bgTertiary : colors.bgPrimary;
                      };

                      if (!canEditProductDetail) {
                        return (
                          <tr
                            key={`${row.projectId}-${row.verificationVersion}-${row.verificationType}`}
                            className={`${getRowStripeColor(index)}  transition-colors ${colors.hoverTableRow}`}
                          >
                            {children}
                          </tr>
                        );
                      }
                      return (
                        <tr
                          key={`${row.projectId}-${row.verificationVersion}-${row.verificationType}`}
                          draggable
                          onDragStart={(e) => handleDragStart(e, row)}
                          onDragEnd={handleDragEnd}
                          className={`${getRowStripeColor(index)} cursor-move transition-colors ${colors.hoverTableRow}`}
                        >
                          {children}
                        </tr>
                      );
                    }}
                  />
                )}
              </div>
            ) : (
              <div className={`flex items-center justify-center h-32 ${colors.bgSecondary} rounded-lg`}>
                <p className={`text-sm ${colors.textSecondary}`}>
                  {t("product.select-group-to-view")}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Assign Project Modal */}
      <AssignProjectModal
        isOpen={isAssignProjectModalOpen}
        onClose={() => setIsAssignProjectModalOpen(false)}
        onAssign={handleAssignProject}
        productSeq={productSeq}
        initialSelectedVerifications={verificationsTotal}
      />

      {/* View Merge Modal - Product Level */}
      <ViewMergeModal
        isOpen={isViewMergeModalOpen}
        onClose={() => {
          setIsViewMergeModalOpen(false);
        }}
        mergedComponents={componentsData}
        licensePolicyData={[]} // Empty array: each component has its own licensePolicy
        loading={getComponentsList.isPending}
        productSeq={productSeq}
        productName={displayProductData?.productName || ""}
        isFromGroup={false}
        showIcon={false}
        editableFrozenVexSnapshot={canEditProductVex}
      />

      {/* Group View Merge Modal */}
      <ViewMergeModal
        isOpen={isGroupViewMergeModalOpen}
        onClose={() => {
          setIsGroupViewMergeModalOpen(false);
        }}
        mergedComponents={groupMergedComponents}
        licensePolicyData={[]} // Empty array: each component has its own licensePolicy
        loading={getGroupComponentsList.isPending}
        productSeq={productSeq}
        productName={displayProductData?.productName || ""}
        groupName={activeGroupId === 'default' ? 'defaultgroup' : groupData[activeGroupId]?.name || ''}
        groupProductSeq={activeGroupId && activeGroupId !== 'default' ? activeGroupId : null}
        isFromGroup={true}
        showIcon={false}
        editableFrozenVexSnapshot={canEditProductVex}
      />

      {/* Document Info Modal */}
      <DocumentInfoModal
        isOpen={isDocumentInfoModalOpen}
        onClose={() => {
          setIsDocumentInfoModalOpen(false);
          setSelectedVerificationForDocumentInfo(null);
          setDocumentInfoComponents([]);
          setSelectedLicensePolicySeq(null);
          setLicensePolicyData(null);
        }}
        verification={selectedVerificationForDocumentInfo}
        components={documentInfoComponents}
        licensePolicyData={licensePolicyData}
        loading={documentInfoLoading}
        productSeq={productSeq}
      />

      {/* Add Group Modal */}
      <AddGroupModal
        isOpen={isAddGroupModalOpen}
        onClose={() => setIsAddGroupModalOpen(false)}
        onCreateGroup={handleCreateGroup}
      />

      {/* Edit Group Modal */}
      <AddGroupModal
        isOpen={isEditGroupModalOpen}
        onClose={() => {
          setIsEditGroupModalOpen(false);
          setEditingGroup(null);
        }}
        onUpdateGroup={handleUpdateGroup}
        editMode={true}
        initialData={editingGroup}
      />

      {/* Delete Group Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={isDeleteGroupModalOpen}
        onClose={handleCancelDeleteGroup}
        onConfirm={handleConfirmDeleteGroup}
        title={t("product.delete-group-confirmation-title")}
        description={t("product.delete-group-confirmation-description")}
        loading={deleteGroupProduct.isPending}
      />

      {/* Delete Verification Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={isDeleteVerificationModalOpen}
        onClose={handleCancelDeleteVerification}
        onConfirm={handleConfirmDeleteVerification}
        title={t("product.delete-verification-confirmation-title")}
        description={t("product.delete-verification-confirmation-description")}
        loading={deleteVerification.isPending}
      />

      {/* Clone Product Confirmation Modal */}
      <ConfirmActionModal
        isOpen={showCloneModal}
        onClose={handleCloneModalClose}
        onConfirm={handleCloneConfirm}
        title={t("product.clone-product-confirmation-title")}
        description={t("product.clone-product-confirmation-description")}
        confirmText={t("common.yes-clone-it")}
        loading={cloneProduct.isPending}
      />

      {/* Change Default Product Confirmation Modal */}
      <ConfirmActionModal
        isOpen={showDefaultProductConfirmModal && defaultProduct != null}
        onClose={handleCancelDefaultProductChange}
        onConfirm={handleConfirmDefaultProductChange}
        title={t("product.change-default-product-confirmation-title")}
        description={t("product.change-default-product-confirmation-description", {
          productName: defaultProduct?.productName,
        })}
        confirmText={t("common.yes")}
        loading={updateProduct.isPending}
        data-testid="change-default-product-confirmation-modal"
      />
    </div>
  );
}
